import { scopedChannelApi } from '@/session/scopedClients';
import type { RequestScope } from '@/session/scopedTransport';
import type { ChannelApiResponse } from '@/types/endpoints';
import type { MappingValueType, PostingTemplateMapping as PostingTemplateMappingRecord } from '@/types/postingTemplate';
import { utils } from '@/utils';
import type { ReadRequestOptions } from '../requestOptions';

// The shared editor type is the source of truth for the mapping shape. The raw
// atts/posting_template payload additionally carries the legacy `delimiter`
// alias for value_delimiter and can serialize value_type as null.
export interface PostingTemplateMapping extends Omit<PostingTemplateMappingRecord, 'value_type'> {
  value_type?: MappingValueType | null;
  delimiter?: string | null;
}

export type PostingTemplateData = {
  grouping_id?: number;
  mappings: PostingTemplateMapping[];
};

type PostingTemplateApiEnvelope = {
  data?: {
    grouping_id?: number;
    mappings?: PostingTemplateMapping[];
  };
};

export async function getPostingTemplate(
  scope: RequestScope,
  args: {
    subscriptionId: number;
    postingTemplateId: number;
  },
  options: ReadRequestOptions
): Promise<ChannelApiResponse<PostingTemplateData>> {
  try {
    if (!args.subscriptionId || args.subscriptionId < 1) {
      throw new Error('Valid subscriptionId is required');
    }
    if (!args.postingTemplateId || args.postingTemplateId < 1) {
      throw new Error('Valid postingTemplateId is required');
    }

    const res = await scopedChannelApi(scope).get<PostingTemplateApiEnvelope>(`atts/posting_template/${args.postingTemplateId}`, {
      ...options,
      headers: {
        'x-astur-subscription-id': args.subscriptionId.toString(),
      },
    });

    const ptData = res.data?.data;
    const grouping_id = typeof ptData?.grouping_id === 'number' ? ptData.grouping_id : undefined;
    const mappings: PostingTemplateMapping[] = Array.isArray(ptData?.mappings) ? ptData!.mappings : [];

    return { data: { grouping_id, mappings }, status: res.status };
  } catch (e) {
    return utils.error.handleAxiosError(e);
  }
}
